import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { colors } from '../../../theme';

export interface FooterProps {
  className?: string;
}

/**
 * Footer navigation.
 *
 * Two rules, both from the Wave A audit (ADR-007 S1.2 / S10.1):
 *
 *  1. Every `to` must resolve to a registered route. Seventeen links here fell
 *     through to the 404 catch-all, including /terms and /privacy — which
 *     registration asks users to accept. `route-integrity.test.ts` now fails
 *     the build if that recurs.
 *  2. A link to a page that does not exist is removed, not left broken. The
 *     entries below marked with a story ID return when that story ships.
 *
 * Social links are external URLs and use `href`, not router `to` — they were
 * previously routed internally, so they 404'd instead of leaving the site.
 */
interface FooterLink {
  label: string;
  /** Internal route. Mutually exclusive with `href`. */
  to?: string;
  /** External destination. */
  href?: string;
}

const SOCIAL_URLS = {
  linkedin: 'https://www.linkedin.com/company/iqmaxx',
  facebook: 'https://www.facebook.com/iqmaxx',
  instagram: 'https://www.instagram.com/iqmaxx',
};

const FOOTER_COLS: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: 'Aprende',
    links: [
      { label: 'Ver clases', to: '/courses' },
      // Tutoring / Homeschool land with the catalog facets — no page yet.
    ],
  },
  {
    title: 'Enseña',
    links: [
      { label: 'Enseña en IQMaxx', to: '/teach' },
      { label: 'Crear una cuenta', to: '/register' },
    ],
  },
  {
    title: 'Soporte',
    links: [
      { label: 'Centro de ayuda', to: '/help' },
      { label: 'Contacto', to: '/contact' },
      { label: 'Aviso de Privacidad', to: '/privacy' },
      { label: 'Términos del Servicio', to: '/terms' },
      // Safety returns with S4.1 (safety readiness).
    ],
  },
  {
    title: 'Síguenos',
    links: [
      { label: 'LinkedIn', href: SOCIAL_URLS.linkedin },
      { label: 'Facebook', href: SOCIAL_URLS.facebook },
      { label: 'Instagram', href: SOCIAL_URLS.instagram },
    ],
  },
];

const linkStyle: React.CSSProperties = {
  color: 'rgba(255,255,255,0.85)',
  textDecoration: 'none',
  fontSize: '0.875rem',
};

export const Footer: React.FC<FooterProps> = () => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: colors.primary.main,
        color: 'white',
        pt: 6,
        pb: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {FOOTER_COLS.map((col) => (
            <Grid item xs={6} sm={4} md key={col.title}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: 'white', mb: 1.5, fontSize: '0.95rem' }}
              >
                {col.title}
              </Typography>
              {col.links.map((link) => (
                <Box key={link.label} sx={{ mb: 0.75 }}>
                  {link.href ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.to as string} style={linkStyle}>
                      {link.label}
                    </Link>
                  )}
                </Box>
              ))}
            </Grid>
          ))}
        </Grid>
        <Box
          sx={{
            mt: 5,
            pt: 3,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            © {new Date().getFullYear()} IQMaxx. Todos los derechos reservados.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
